/**
 * ATT-2872 admin half — `useTenantDiscovery()` React hook.
 *
 * Boot-time tenant identity + routing hydration. ATT-3383: tenant resolution is
 * registry-only, so discovery fires for EVERY host (no CSV allowlist gate) —
 * `config/tenants.ts#getTenantConfig` returns the first-paint bootstrap until
 * this hook populates the host-keyed routing cache and the branding store.
 *
 * Behavior:
 *   - SSR: no-op (`useEffect` runs only on the client).
 *   - Client: call the shared `fetchTenantSettings` core, racing it against a
 *     `DISCOVERY_TIMEOUT_MS` deadline so a hung request can't wedge the hook
 *     forever. On success map rows into the store via `setDiscovered` and
 *     populate the routing cache when the registry returned a `domain`; on
 *     failure or timeout set `source="failed"` so the app shows an explicit
 *     loading/retry state instead of a silent empty-branding render
 *     (design.md — ATT-3383 graceful-failure decision).
 *
 * Retry: a full page reload remounts `<App />` and the hook fires again. The
 * `retry` callback returned here re-runs discovery in place (driven by the
 * loading/retry UI's retry affordance) without a reload. Client-side route
 * changes do NOT remount `<App />` and therefore do NOT trigger discovery.
 *
 * Returns the live store state (subscribed) so consumers re-render on update,
 * plus a `retry` callback for the loading/retry UI.
 */

import { useCallback, useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { useTenantDiscoveryStore } from "./store";
import { fetchTenantSettings } from "../tenantSettings/fetch";
import { fetchTenantBrandingByHost } from "../tenantOverride/fetchBranding";
import { coerceDiscoverySetting } from "./coerce";
import { setRoutingForHost } from "./routingCache";
import { normalizeHost } from "../../config/tenants";

/**
 * ATT-3785: point at which a still-in-flight discovery is reported as SLOW.
 * The run keeps going — this only flips `slow` on the store so the gate can
 * explain the wait. This is where the old hard deadline used to be.
 */
export const DISCOVERY_SLOW_AFTER_MS = 5000;

/**
 * Hard deadline for the boot-time discovery fetch: past this the store
 * transitions to `source="failed"` and the app renders the explicit
 * loading/retry state (ATT-3383).
 *
 * ATT-3785 — why this is 25 s and not the original 5 s: staging runs the
 * consolidated backend at `min_instances = 0` with `startup_cpu_boost = false`,
 * so the first request after an idle period pays a full Cloud Run cold start.
 * The server loads the Secret Manager and Firestore SDKs and awaits
 * `bootstrapRegistrySnapshot()`'s Firestore round trip BEFORE it ever calls
 * `listen`, all on throttled CPU. Cloud Run queues the request behind that for
 * up to its 240 s startup-probe budget — the request is not failing, it is
 * waiting. A 5 s client deadline lost that race every time and showed the
 * retry screen on first load, which a reload then "fixed" because the instance
 * was warm. 25 s covers a realistic cold start while still bounding a genuinely
 * dead backend; `DISCOVERY_SLOW_AFTER_MS` keeps the wait legible in between.
 *
 * NOTE: this is a soft cancel — the underlying request continues in the
 * background. Its late resolution is NOT discarded: it heals the failed state
 * (see `runDiscovery`), so a user never has to click "Try again" for a response
 * we are already holding. Only a SUPERSEDED run's resolution is dropped. We do
 * not thread an `AbortSignal` through `fetchTenantSettings` because that
 * function is shared with the ATT-2871 overlay path and a signature change
 * would ripple across two consumers for no behavioral gain.
 */
export const DISCOVERY_TIMEOUT_MS = 25_000;

export type TenantDiscoveryView = {
  source: "bootstrap" | "discovered" | "failed";
  /** ATT-3785 — in flight, past the soft threshold. See the store's `slow`. */
  slow: boolean;
  displayName: string;
  hideLanguages: string[];
  featureFlags: string[];
  logoUrl: string | null;
};

function mergeCoercedRows(rows: { key: string; value?: unknown }[]): Partial<
  Pick<TenantDiscoveryView, "displayName" | "hideLanguages" | "featureFlags" | "logoUrl">
> {
  return rows.reduce<Partial<
    Pick<TenantDiscoveryView, "displayName" | "hideLanguages" | "featureFlags" | "logoUrl">
  >>((acc, row) => {
    const coerced = coerceDiscoverySetting(row.key, row.value);
    if (!coerced) return acc;
    if (coerced.key === "display_name") return { ...acc, displayName: coerced.value as string };
    if (coerced.key === "hide_languages") return { ...acc, hideLanguages: coerced.value as string[] };
    if (coerced.key === "feature_flags") return { ...acc, featureFlags: coerced.value as string[] };
    if (coerced.key === "logo_url") return { ...acc, logoUrl: coerced.value as string | null };
    return acc;
  }, {});
}

/** Capture a Sentry warning tagged with the discovery failure mode (ATT-3383). */
function captureDiscoveryFailure(mode: "timeout" | "empty"): void {
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("event", "tenant-discovery-failed");
    scope.setTag("failure_mode", mode);
    if (typeof window !== "undefined") {
      scope.setExtras({ host: normalizeHost(window.location.hostname) });
    }
    Sentry.captureMessage("tenant-discovery-failed", { level: "warning" });
  });
}

/**
 * ATT-3785 — run generation. Only the NEWEST run may write to the store: a run
 * that has been superseded (by `retry()`, a dependency change, or a StrictMode
 * double-invoke) is stale and every one of its callbacks is dropped.
 *
 * This is what makes it safe to honor a LATE response. The old code used a
 * one-shot `settled` flag, which conflated two very different things: "this run
 * was replaced" (must be dropped) and "this run passed its deadline" (must NOT
 * be dropped — the answer is still the one we asked for). Splitting them lets a
 * slow cold start heal the failure screen while a superseded run still cannot
 * clobber the run that replaced it.
 */
let activeRunId = 0;
let disposeActiveRun: (() => void) | null = null;

/**
 * Test-only: drop the module-level run generation so cases can't leak an
 * in-flight run into each other. The `isStale()` guard already makes leakage
 * harmless, but jest keeps one module registry per test FILE, so without this
 * the generation counter and a live dispose survive across cases — resetting
 * makes isolation a property of the setup rather than of the guard.
 */
export function __resetForTests(): void {
  disposeActiveRun?.();
  activeRunId = 0;
  disposeActiveRun = null;
}

/**
 * Run one discovery attempt against the current host. Drives the store through
 * at most: slow (still in flight) → discovered | failed, and can still reach
 * `discovered` AFTER `failed` when a cold start finally answers.
 *
 * Returns a dispose function that cancels the run — clearing its timers and
 * dropping any resolution still to come — so unmount/teardown never writes.
 */
function runDiscovery(brandingHostOverride?: string): () => void {
  if (typeof window === "undefined") return () => undefined;

  // Supersede whatever is still in flight: clear its timers so a stale deadline
  // cannot fire over this run.
  disposeActiveRun?.();

  activeRunId += 1;
  const runId = activeRunId;
  let cancelled = false;
  const isStale = (): boolean => cancelled || runId !== activeRunId;

  const slowHandle = window.setTimeout(() => {
    if (isStale()) return;
    useTenantDiscoveryStore.getState().setSlow();
  }, DISCOVERY_SLOW_AFTER_MS);

  const timeoutHandle = window.setTimeout(() => {
    if (isStale()) return;
    // Deliberately does NOT settle the run: the request is still outstanding
    // and its eventual answer will heal this state.
    captureDiscoveryFailure("timeout");
    useTenantDiscoveryStore.getState().setFailed();
  }, DISCOVERY_TIMEOUT_MS);

  const clearTimers = (): void => {
    window.clearTimeout(slowHandle);
    window.clearTimeout(timeoutHandle);
  };

  const dispose = (): void => {
    cancelled = true;
    clearTimers();
    if (disposeActiveRun === dispose) disposeActiveRun = null;
  };
  disposeActiveRun = dispose;

  // ATT-3464: on the central auth host the REQUEST host is deliberately not a
  // tenant, so the host-keyed discovery below resolves UNKNOWN_DOMAIN and the
  // gate traps the user forever. The page hands us the origin tenant it already
  // validated server-side; resolve THAT tenant's branding instead.
  //
  // Why the branding-by-host query and not a header override: the normal path is
  // gated by the ATT-2866 `X-Tenant-Domain` <-> `Origin` cross-check, and
  // `Origin` is browser-controlled — claiming another tenant from this host is
  // rejected as TENANT_MISMATCH by construction. `tenantBrandingByHost` is the
  // purpose-built, structurally cross-check-exempt resolver ATT-3384 already
  // uses for exactly this shape (render tenant A's branding from host B).
  //
  // BRANDING ONLY — deliberately no `setRoutingForHost`. The central host must
  // never adopt a tenant's `apiUrl`/`cookieDomain`; the only request this page
  // makes is a same-origin `signIn()`.
  if (brandingHostOverride) {
    fetchTenantBrandingByHost(brandingHostOverride, "admin")
      .then((branding) => {
        if (isStale()) return;
        clearTimers();
        if (!branding) {
          // A missing logo must never block a sign-in. Mark the run resolved so
          // the gate releases and the page renders unbranded (see
          // TenantDiscoveryGate — the central host never blocks).
          captureDiscoveryFailure("empty");
          useTenantDiscoveryStore.getState().setFailed();
          return;
        }
        useTenantDiscoveryStore.getState().setDiscovered({
          displayName: branding.displayName ?? "",
          logoUrl: branding.logoUrl,
          featureFlags: branding.featureFlags,
          hideLanguages: branding.hideLanguages,
        });
      })
      .catch(() => {
        if (isStale()) return;
        clearTimers();
        captureDiscoveryFailure("empty");
        useTenantDiscoveryStore.getState().setFailed();
      });

    return dispose;
  }

  fetchTenantSettings()
    .then((res) => {
      if (isStale()) return;
      clearTimers();
      if (!res) {
        // `fetchTenantSettings` already captured network / malformed failures
        // via Sentry; tag the empty-result mode for completeness.
        captureDiscoveryFailure("empty");
        useTenantDiscoveryStore.getState().setFailed();
        return;
      }
      // Fail-together (design.md §D6): the shared fetch core returns null when
      // the `domain` object is malformed, so reaching here means branding AND
      // (optional) routing both parsed. Populate the routing cache (when the
      // registry returned a `domain`) and the branding store together. An
      // absent `domain` is not a failure — the host keeps its bootstrap routing
      // (apiUrl = NEXT_PUBLIC_API_BASE) and only branding is applied.
      const merged = mergeCoercedRows(res.settings);
      if (res.domain) {
        setRoutingForHost(normalizeHost(window.location.hostname), {
          apiUrl: res.domain.apiUrl,
          cookieDomain: res.domain.cookieDomain,
          portal: res.domain.portal,
          publicBase: res.domain.publicBase,
        });
      }
      useTenantDiscoveryStore.getState().setDiscovered(merged);
    })
    .catch(() => {
      // `fetchTenantSettings` already captures network / parse errors via
      // Sentry and resolves to null; this catch is a belt-and-braces guard for
      // the unlikely case of an unhandled rejection from the underlying
      // graphql-request client (e.g. an abort propagated from upstream).
      if (isStale()) return;
      clearTimers();
      captureDiscoveryFailure("empty");
      useTenantDiscoveryStore.getState().setFailed();
    });

  return dispose;
}

/**
 * @param brandingHostOverride ATT-3464 — resolve branding for THIS host instead
 *   of the request host. Set only by the central auth host's `/auth/start`,
 *   which validated the origin tenant server-side. Omitted everywhere else, and
 *   omitted entirely while `NEXT_PUBLIC_CENTRAL_AUTH_HOST` is unset — the
 *   default path below is then byte-for-byte today's behavior.
 */
export function useTenantDiscovery(
  brandingHostOverride?: string,
): TenantDiscoveryView & { retry: () => void } {
  const state = useTenantDiscoveryStore();

  useEffect(() => runDiscovery(brandingHostOverride), [brandingHostOverride]);

  const retry = useCallback(() => {
    // Reset to the loading (bootstrap) state and re-run discovery in place so
    // the loading/retry UI can recover without a full page reload.
    useTenantDiscoveryStore.getState().setBootstrap({});
    runDiscovery(brandingHostOverride);
  }, [brandingHostOverride]);

  return {
    source: state.source,
    slow: state.slow,
    displayName: state.displayName,
    hideLanguages: state.hideLanguages,
    featureFlags: state.featureFlags,
    logoUrl: state.logoUrl,
    retry,
  };
}
